import * as Sentry from '@sentry/react';
import { ErrorInfo } from 'react';

import { getUserDisplayName, isHeadTrainer } from '../models/user/utils';

import type { User } from '../models/user/_types';

const isStaging = import.meta.env.MODE === 'staging';
const isProd = import.meta.env.MODE === 'production';

/**
 * Initialize Sentry with proper configuration for different environments
 */
export function initializeSentry() {
  // Detect environment

  // Determine Sentry environment
  let environment = 'development';
  if (isProd) {
    environment = 'production';
  } else if (isStaging) {
    environment = 'staging';
  }

  // Initialize Sentry as early as possible
  Sentry.init({
    dsn: 'https://ea59a69ce1294076bd89672700ca3791@o325104.ingest.us.sentry.io/1829475',
    environment,
    // Adds request headers and IP for users, for more info visit:
    // https://docs.sentry.io/platforms/javascript/guides/react/configuration/options/#sendDefaultPii
    sendDefaultPii: true,
    // Replay configuration - start with 0% sampling, enable only for trainers
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [
      // Send console.log, console.error, and console.warn calls as logs to Sentry
      Sentry.consoleLoggingIntegration({ levels: ['log', 'error', 'warn'] }),
      // Session Replay integration
      Sentry.replayIntegration(),
    ],
    //tunnel: '/tunnel',
    _experiments: {
      enableLogs: true,
    },
  });
}

/**
 * Set user context and enable session replay for trainers
 */
export function setupSentryUserContext(user: User | null | undefined) {
  if (user) {
    // Set user context in Sentry
    Sentry.setUser({
      id: user.id?.toString(),
      email: user.email,
      username: getUserDisplayName(user),
    });

    // If user is a trainer AND in production, enable session replay
    if (isHeadTrainer(user) && isProd /* || isStaging*/) {
      // Tag user as trainer for better filtering in Sentry
      Sentry.getCurrentScope().setTag('userType', 'trainer');

      // Get the replay integration and ensure recording for trainers
      const replay = Sentry.getReplay();
      if (replay) {
        // flush() will either flush existing replay or start new session if stopped
        replay.flush();
      }
    }
  } else {
    // Clear user context when logged out
    Sentry.setUser(null);
  }
}

/**
 * Capture an exception to Sentry with additional context
 */
export function captureException(error: Error, errorInfo: ErrorInfo) {
  Sentry.captureReactException(error, errorInfo);
}

/**
 * Create a custom span for performance monitoring
 */
export function createSpan<T>(
  operation: string,
  name: string,
  fn: (span: Sentry.Span) => T,
  attributes?: Record<string, any>
): T {
  return Sentry.startSpan(
    {
      op: operation,
      name,
      attributes,
    },
    fn
  );
}

/**
 * Add breadcrumb for debugging context
 */
export function addBreadcrumb(
  message: string,
  category?: string,
  level?: Sentry.SeverityLevel
) {
  Sentry.addBreadcrumb({
    message,
    category: category || 'custom',
    level: level || 'info',
    timestamp: Date.now() / 1000,
  });
}

// Re-export commonly used Sentry functions for convenience
export { Sentry };
